import { useState, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';

type Permission = 'default' | 'granted' | 'denied';

/**
 * Wraps the browser Notification API for web (non-Capacitor) platforms.
 * Call requestPermission() from a user gesture (e.g. button click) — browsers
 * require a gesture before showing the OS permission prompt.
 */
export function useWebNotifications() {
  const isWeb = !Capacitor.isNativePlatform();
  const supported = isWeb && typeof window !== 'undefined' && 'Notification' in window;

  const [permission, setPermission] = useState<Permission>(
    supported ? (Notification.permission as Permission) : 'denied'
  );

  const requestPermission = useCallback(async (): Promise<Permission> => {
    if (!supported) return 'denied';
    const result = await Notification.requestPermission();
    setPermission(result as Permission);
    return result as Permission;
  }, [supported]);

  const showNotification = useCallback(
    (title: string, body?: string, onClick?: () => void, tag?: string) => {
      if (!supported || Notification.permission !== 'granted') return;
      const n = new Notification(title, {
        body,
        icon: '/logo.png',
        badge: '/logo.png',
        // Same tag replaces an earlier one (another tab showing the same item)
        tag,
      });
      if (onClick) {
        n.onclick = () => {
          window.focus();
          onClick();
          n.close();
        };
      }
    },
    [supported]
  );

  return { permission, requestPermission, showNotification };
}
